import { ApiError, NotAuthenticatedError } from "@/common/errors"
import { tokenStore } from "@/lib/idb/tokenStore"
import {ensure} from "@/common/utils";
import {cognitoClient} from "@/lib/cognito-client";
import {InitiateAuthCommand} from "@aws-sdk/client-cognito-identity-provider";

function getBaseURL(): string {
  // During build time, environment variables might not be available
  // Return a placeholder that will be replaced at runtime
  if (typeof window === 'undefined' && !process.env.NEXT_PUBLIC_API_URL) {
    return 'http://build-time-placeholder'
  }
  return ensure(process.env.NEXT_PUBLIC_API_URL)
}

let baseURL: string | null = null

function ensureBaseURL(): string {
  if (!baseURL) {
    baseURL = getBaseURL()
  }
  return baseURL
}

export function setBaseURL(url: string): void {
  baseURL = url
}

/**
 * WebSocket の接続先を組み立てるために API のベースURLを公開する。
 * fetch は customClient が解決するが、WebSocket は同じ仕組みに乗せられないため。
 */
export function getApiBaseURL(): string {
  return ensureBaseURL()
}

let refreshTokenPromise: Promise<string> | null = null

/**
 * トークンの更新を1本にまとめる。同時に何本もリクエストが走っても Cognito には1回だけ頼み、
 * 終わったら空に戻す。戻さないと、一度更新した結果（古いトークン）を使い回し続ける
 */
function refreshOnce(): Promise<string> {
  refreshTokenPromise =
    refreshTokenPromise ??
    refreshToken().finally(() => {
      refreshTokenPromise = null
    })
  return refreshTokenPromise
}

async function redirect(): Promise<never> {
  await tokenStore.clearAll()
  throw new NotAuthenticatedError()
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("Content-Type")

  const data = contentType?.startsWith("application/json")
    ? await response.json()
    : await response.blob()

  return data as T
}

export async function customClient<T>(
  url: string,
  options?: RequestInit
): Promise<T> {
  const keepalive = url.startsWith("/public/lead-session-events") && options?.method === "POST"

  let accessToken = null
  try {
    accessToken = await tokenStore.getAccessToken()
  } catch {
      // 何もしない
  }

  const fullUrl = `${ensureBaseURL()}${url}`

  // アクセストークンの期限（1時間）が切れていても、リフレッシュトークンが残っていれば
  // 先に更新してから送る。これまでは認証なしで送って401になり、ログイン情報ごと消していた
  // （管理画面を1時間以上開いたまま操作すると、何をしても失敗していた）
  if (!accessToken && (await tokenStore.getRefreshToken().catch(() => null))) {
    accessToken = await refreshOnce().catch(() => null)
  }

  if (!accessToken) {
    const response = await fetch(fullUrl, {
      ...options,
      keepalive,
    })

    if (response.ok) {
      return parseResponse<T>(response)
    }

    if (response.status === 401) {
      await redirect()
    }

    throw new ApiError(response)
  }

  const headers = new Headers(options?.headers)
  headers.set("Authorization", `Bearer ${accessToken}`)

  const response = await fetch(fullUrl, {
    ...options,
    headers,
    keepalive,
  })

  if (response.ok) {
    return parseResponse<T>(response)
  }

  if (response.status !== 401) {
    throw new ApiError(response)
  }

  const newAccessToken = await refreshOnce().catch(async (e) => {
    if (e instanceof RefreshTokenNotExistError) {
      throw e
    }
    await redirect()
  })

  headers.set("Authorization", `Bearer ${newAccessToken}`)

  const retryResponse = await fetch(fullUrl, {
    ...options,
    headers,
    keepalive,
  })

  if (retryResponse.ok) {
    return parseResponse<T>(retryResponse)
  }

  if (retryResponse.status === 401) {
    await redirect()
  }

  throw new ApiError(retryResponse)
}

/** JWT の exp から、あと何秒で切れるかを求める。読めなければ Cognito の既定の1時間とみなす */
function secondsUntilExpiry(jwt: string): number {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload.exp === 'number') return Math.max(0, payload.exp - Math.floor(Date.now() / 1000))
  } catch {
    // 読めなければ既定値にする
  }
  return 3600
}

class RefreshTokenNotExistError extends Error {
  constructor() {
    super("refresh token が存在しません")
  }
}

async function refreshToken(): Promise<string> {
  const refreshToken =  await tokenStore.getRefreshToken()
  if (!refreshToken) {
    throw new RefreshTokenNotExistError()
  }

  const authResult = await cognitoClient.send(
      new InitiateAuthCommand({
        ClientId: process.env['NEXT_PUBLIC_AWS_COGNITO_CLIENT_ID'],
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
  )

  const newAccessToken = ensure(authResult.AuthenticationResult?.AccessToken)
  // Cognito は更新時にリフレッシュトークンを返さないのが普通（ローテーションを有効にしたときだけ返る）。
  // 返ってこなければ手元のものを使い続ける。必須として扱うと、更新が毎回失敗してログアウトされる
  const newRefreshToken = authResult.AuthenticationResult?.RefreshToken ?? refreshToken
  // 有効期限（秒）も返らないことがある（手元の cognito-local は返さない）。無ければトークンに書かれた
  // 期限（exp）から求める。必須として扱うと、ここで失敗して更新を諦め、認証なしで送ってしまう
  const newExpiresIn = authResult.AuthenticationResult?.ExpiresIn ?? secondsUntilExpiry(newAccessToken)

  await tokenStore.setTokens(newAccessToken, newRefreshToken, newExpiresIn)

  return newAccessToken
}

export default customClient
